/**
 * PATH: packages/ui/src/mobile/MobileSheet.tsx
 * FILE_ID: UI-MOBILE-SHEET-001
 * PURPOSE: Shared bottom sheet chrome. Action lists wrap this; forms pass children.
 * LANDMINE: Footer pad uses --mobile-dock-h so a body portal cannot sit under MobileDock.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

import { cn } from '../lib/utils'

export const MOBILE_SHEET_FOOTER_PAD =
  'calc(var(--mobile-dock-h, 0px) + env(safe-area-inset-bottom, 0px))'

export interface MobileSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  rootTestId?: string
  sheetTestId?: string
  /** 'full' pins the panel at 92dvh for forms that need the screen; default hugs content. */
  size?: 'auto' | 'full'
  showClose?: boolean
}

export function MobileSheet({
  open,
  onClose,
  title,
  children,
  footer,
  rootTestId,
  sheetTestId,
  size = 'auto',
  showClose = false,
}: MobileSheetProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    queueMicrotask(() => panelRef.current?.focus())
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-end justify-center"
      role="presentation"
      data-testid={rootTestId ?? 'mobile-sheet-root'}
    >
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-black/45"
        onPointerDown={(event) => {
          event.preventDefault()
          onClose()
        }}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Sheet'}
        tabIndex={-1}
        className={cn(
          'relative flex w-full flex-col rounded-t-2xl',
          size === 'full' ? 'h-[92dvh] max-h-[92dvh]' : 'max-h-[80vh]',
          'border border-neutral-200 bg-white shadow-2xl outline-none',
          'dark:border-neutral-800 dark:bg-neutral-950',
        )}
        data-testid={sheetTestId ?? 'mobile-sheet'}
      >
        <div className="flex justify-center pt-2 pb-1" aria-hidden="true">
          <span className="h-1.5 w-10 rounded-full bg-neutral-300 dark:bg-neutral-700" />
        </div>
        {title ? (
          showClose ? (
            <div className="flex shrink-0 items-center justify-between border-b border-neutral-100 px-4 pb-2 pt-1 dark:border-neutral-800">
              <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {title}
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="shrink-0 border-b border-neutral-100 px-4 pb-2 pt-1 dark:border-neutral-800">
              <h2 className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                {title}
              </h2>
            </div>
          )
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-neutral-100 px-4 py-3 dark:border-neutral-800">
            {footer}
          </div>
        ) : null}
        <div aria-hidden="true" style={{ height: MOBILE_SHEET_FOOTER_PAD }} />
      </div>
    </div>,
    document.body,
  )
}
